'use client';

import { ReactNode, useCallback, useMemo, memo } from 'react';
import { useTranslations } from 'next-intl';
import { Position, PRODUCT_PRICES_INFORMATION_MENU_ID } from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import { GlowTextBody, PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { PromotionInformationType, PromotionType } from '../../../../../server';
import { VATLegalMention } from '../../../components';
import { ProductDetailInfoModal } from '../../productDetailModal';
import { ProductDetailPrice } from './ProductDetailPrice';
import { ProductDetailPricesPromotion } from './ProductDetailPricesPromotion';

export type ProductDetailPricesContainerVariant = 'default' | 'small';

type ProductDetailPricesContainerProps = PropsWithFontFamily &
    PropsWithLocale & {
        price?: number;
        pricePerUnit?: string;
        priorPrice?: number;
        originalPrice?: number;
        currency: string;
        priceAlwaysBlack: boolean;
        promotion?: PromotionType;
        discountPercentageLabel?: string;
        enableDiscountInfo: boolean;
        promotionInformation?: PromotionInformationType;
        formattedTotalIndividualPrice?: string;
        variant?: ProductDetailPricesContainerVariant;
        isPriceLegalMentionEnabled: boolean;
    };

export const ProductDetailPricesContainer: ComponentMemo<ProductDetailPricesContainerProps> = memo(
    ({
        price,
        pricePerUnit,
        currency,
        locale,
        fontFamily,
        promotion,
        discountPercentageLabel,
        priorPrice,
        originalPrice,
        enableDiscountInfo,
        priceAlwaysBlack,
        promotionInformation,
        formattedTotalIndividualPrice,
        variant = 'default',
        isPriceLegalMentionEnabled,
    }) => {
        const t = useTranslations('Discover.Pdp.information.price');

        const { addAndOpen: addAndOpenMenu } = useModalActions();

        const priceInfoModal = useMemo(
            () => (
                <ProductDetailInfoModal
                    title={t('pricesModal.label.title')}
                    closeButtonLabel={t('modal.action.close')}
                    productModalId={PRODUCT_PRICES_INFORMATION_MENU_ID}
                    renderInformation={
                        <ProductDetailPricesPromotion
                            priorPrice={priorPrice}
                            price={price}
                            currency={currency}
                            locale={locale}
                            enableDiscountInfo={enableDiscountInfo}
                            pricePerUnit={pricePerUnit}
                            fontFamily={fontFamily}
                            priceAlwaysBlack={priceAlwaysBlack}
                            originalPrice={originalPrice}
                            discountPercentageLabel={discountPercentageLabel}
                            promotion={promotion}
                            displayLegalMentionInformation={true}
                            promotionInformation={promotionInformation}
                            isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                            VATLegalMentionContent={isPriceLegalMentionEnabled && <VATLegalMention />}
                        />
                    }
                />
            ),
            [
                price,
                priorPrice,
                pricePerUnit,
                currency,
                locale,
                fontFamily,
                promotion,
                discountPercentageLabel,
                originalPrice,
                enableDiscountInfo,
                priceAlwaysBlack,
                promotionInformation,
                isPriceLegalMentionEnabled,
                t,
            ],
        );

        const onOpenMenu = useCallback(
            (content: ReactNode) => {
                const menuClassName = 'flex flex-col text-text-brand overflow-hidden';
                addAndOpenMenu(
                    PRODUCT_PRICES_INFORMATION_MENU_ID,
                    SideMenuModal({ side: Position.Right, map: { default: content }, className: menuClassName }),
                );
            },
            [addAndOpenMenu],
        );

        const isProductWithoutPromotion = !originalPrice || price === originalPrice;

        return (
            <>
                {isProductWithoutPromotion ? (
                    <ProductDetailPrice
                        price={price}
                        locale={locale}
                        variant={variant}
                        currency={currency}
                        pricePerUnit={pricePerUnit}
                        fontFamily={fontFamily}
                        promotion={promotion}
                        priceAlwaysBlack={priceAlwaysBlack}
                        displayLegalMentionInformation={false}
                        isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                    />
                ) : (
                    <ProductDetailPricesPromotion
                        priorPrice={priorPrice}
                        price={price}
                        currency={currency}
                        variant={variant}
                        locale={locale}
                        enableDiscountInfo={enableDiscountInfo}
                        pricePerUnit={pricePerUnit}
                        fontFamily={fontFamily}
                        priceAlwaysBlack={priceAlwaysBlack}
                        originalPrice={originalPrice}
                        discountPercentageLabel={discountPercentageLabel}
                        promotion={promotion}
                        onOpenProductPriceModal={() => onOpenMenu(priceInfoModal)}
                        displayLegalMentionInformation={false}
                        promotionInformation={promotionInformation}
                        isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                        className={'relative !p-0'}
                    />
                )}

                {formattedTotalIndividualPrice && (
                    <GlowTextBody text={formattedTotalIndividualPrice} fontFamily={fontFamily} fontWeight={'bold'} />
                )}

                {isPriceLegalMentionEnabled && <VATLegalMention />}
            </>
        );
    },
);

ProductDetailPricesContainer.displayName = 'ProductDetailPricesContainer';
